import { useEffect, useMemo, useState } from 'react'
import { FormikProvider, useFormik } from 'formik'
import { DateTime } from 'luxon'

import { Button, Form, Popup, Image, Text } from '@/ui'
import { TypographyStyles } from '@/styles'
import { PayrollInterestScreenName } from './PayrollInterestFlow'
import { markUserActionItemCompleteIfExists } from '@/features/user/dashboard/lib/service'
import { PostOnboardingTaskIdents } from '@/features/onboarding/config'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { selectIsCurrentUserScorp } from '@/selectors/user.selectors'
import { describeBaseAndPerPersonCost } from '../../payrollPrice.copy'
import { usePayrollPrices } from '../../payrollPrice.hooks'
import { PayrollSetup } from '@/reducers/auth/userReducer'
import {
  fetchUserOnboardingStepsIfNeeded,
  updateUserOnboardingStep,
} from '@/features/onboarding/user-onboarding-steps/onboarding.actions'
import { getUserStepIdByOnboardingStepId } from '@/features/onboarding/user-onboarding-steps/onboarding.selectors'
import { GettingStartedOnboardingStep } from '@/features/onboarding/user-onboarding-steps/onboarding.reducer'
import {
  GustoPlan,
  PayrollNotInterestedReason,
  submitPayrollInterestSurvey,
  UsedPayrollInPast,
} from '../payrollInterestSurvey.actions'
import { LearnMorePayrollModal } from '../LearnMorePayrollModal'
import { FormFlowFooter } from '@/components/form-flow/FormFlowFooter'

// TODO: Migrate to V2
import { LabelDescription } from '@/componentsV1/BaseComponents/Input'
import {
  FormikLabelError,
  FormikScrollOnError,
  FormikTextArea,
  getFieldNames,
  makeReqStringSchema,
} from '@/componentsV1/BaseComponents'
import { selectUserHasPermission } from '@/reducers/auth/permission.selectors'
import { ProductFeatureId } from '@/actions/permissionActions'

export const PayrollInterestForm = ({
  setScreen,
}: {
  setScreen: (screen: string) => void
}) => {
  const dispatch = useAppDispatch()
  const hasFreePayrollWithSubscription = useReselector(
    selectUserHasPermission,
    ProductFeatureId.payrollIncludedForFreeForSelf
  )
  const isScorp = useReselector(selectIsCurrentUserScorp)
  const payrollPrices = usePayrollPrices()
  const { seat, yearly } = payrollPrices
  const [infoModalOpen, setInfoModalOpen] = useState(false)

  const gettingStartedStepId = useReselector(
    getUserStepIdByOnboardingStepId,
    GettingStartedOnboardingStep.payrollInterest
  )

  useEffect(() => {
    dispatch(fetchUserOnboardingStepsIfNeeded())
  }, [dispatch])

  const formik = useFormik({
    initialValues: {
      payrollSetup: undefined as PayrollSetup | undefined,
      otherProvider: undefined as string | undefined,
      gustoPlan: undefined as string | undefined,
      interestedInGep: undefined as boolean | undefined,
      notInterestedReason: undefined as PayrollNotInterestedReason | undefined,
      usedGustoInPast: undefined as UsedPayrollInPast | undefined,
      otherNotInterestedReason: undefined as string | undefined,
      missingFeaturesNotInterestedReason: undefined as string | undefined,
      // not saved in DB
      needPayrollForOthers: undefined as boolean | undefined,
      needPayrollForSelf: undefined as boolean | undefined,
      needPayrollForEmployees: undefined as boolean | undefined,
      needPayrollForContractors: undefined as boolean | undefined,
      // not saved in DB
      needPayrollForOtherCheckbox: undefined as boolean | undefined,
      needPayrollForOther: undefined as string | undefined,
      multiplePayRates: undefined as boolean | undefined,
      gustoSponsoredBenefits: undefined as boolean | undefined,
      timeTracking: undefined as boolean | undefined,
      commissionOnlyEmployees: undefined as boolean | undefined,
    },
    onSubmit: async ({
      payrollSetup,
      otherProvider,
      gustoPlan,
      interestedInGep,
      notInterestedReason,
      usedGustoInPast,
      otherNotInterestedReason,
      missingFeaturesNotInterestedReason,
      needPayrollForSelf,
      needPayrollForEmployees,
      needPayrollForContractors,
      needPayrollForOther,
      multiplePayRates,
      gustoSponsoredBenefits,
      timeTracking,
      commissionOnlyEmployees,
    }) => {
      const submitSurveyRes = await dispatch(
        submitPayrollInterestSurvey({
          payrollSetup,
          payrollProvider: otherProvider,
          gustoPlan,
          interestedInGepAt: interestedInGep
            ? new Date().toISOString()
            : undefined,
          gepRequestFormSubmittedAt: new Date().toISOString(),
          notInterestedReason,
          otherNotInterestedReason,
          missingFeaturesNotInterestedReason,
          needPayrollForSelf,
          needPayrollForEmployees,
          needPayrollForContractors,
          needPayrollForOther,
          multiplePayRates,
          gustoSponsoredBenefits,
          timeTracking,
          commissionOnlyEmployees,
          usedGustoInPast,
        })
      )

      if (!submitSurveyRes) {
        return
      }

      markUserActionItemCompleteIfExists(
        PostOnboardingTaskIdents.SET_UP_PAYROLL_ACCESS
      )

      if (gettingStartedStepId) {
        dispatch(
          updateUserOnboardingStep({
            id: gettingStartedStepId,
            updatableFields: { completedAt: DateTime.now().toISO() },
          })
        )
      }

      setScreen(PayrollInterestScreenName.OUTRO)
    },
  })

  const { values, submitForm, isSubmitting } = formik
  const fieldNames = getFieldNames(formik)

  const interestedInGEPLabel = useMemo(() => {
    if (values.payrollSetup === PayrollSetup.gusto) {
      if (hasFreePayrollWithSubscription) {
        return 'Are you interested in migrating your payroll to Heard?'
      } else {
        return 'Would you like to migrate your Gusto payroll to Heard?'
      }
    } else if (
      values.payrollSetup === PayrollSetup.other &&
      hasFreePayrollWithSubscription
    ) {
      return 'Are you interested in switching your payroll to Heard?'
    }
    return 'Are you interested in setting up payroll on Heard?'
  }, [values.payrollSetup, hasFreePayrollWithSubscription])

  const interestedInGEPDescription = useMemo(() => {
    const baseAndPerPersonCost = describeBaseAndPerPersonCost(payrollPrices)
    if (values.payrollSetup === PayrollSetup.gusto) {
      if (hasFreePayrollWithSubscription) {
        const saving = yearly
          ? `Save up to ${yearly} annually by switching to Heard.`
          : 'Save by switching to Heard.'
        return `${saving} We offer a streamlined set of features necessary to ensure you are compliant. Depending upon your current plan, not all features may be supported in Heard Payroll.`
      } else {
        return `Migrating allows you to manage your payroll directly within Heard. This is ${baseAndPerPersonCost}.`
      }
    } else if (hasFreePayrollWithSubscription) {
      const valueNote = yearly ? ` (value of ${yearly} annually)` : ''
      return `Heard has an integrated payroll via the Gusto platform${valueNote}. We offer a streamlined set of features necessary to ensure you are compliant.`
    }
    return `Heard has an integrated payroll via the Gusto platform. This is ${baseAndPerPersonCost}.`
  }, [
    values.payrollSetup,
    hasFreePayrollWithSubscription,
    payrollPrices,
    yearly,
  ])

  const perPersonCost = seat
    ? `a cost of ${seat}/individual per month`
    : 'a monthly cost per individual'

  return (
    <FormikProvider value={formik}>
      <div className="flex flex-col gap-6 max-w-[80%] mx-auto">
        <FormikScrollOnError />

        <Text variant={TypographyStyles.H1}>
          Tell us about your payroll needs
        </Text>

        <div className="bg-yellow-25 flex justify-center items-center py-5">
          <Image
            src="https://heard-images.s3.amazonaws.com/assets/writing_check.svg"
            className="w-[180px] h-[180px]"
            alt="Writing Check"
          />
        </div>
        {/* 
        <FormikLabelError
          name={fieldNames.payrollSetup}
          label="Do you currently have a payroll provider?"
          schema={makeReqStringSchema()}
        /> */}
        <Form.Select
          name={fieldNames.payrollSetup}
          label="Do you currently have a payroll provider?"
          schema={makeReqStringSchema()}
          placeholder="Select an option"
          options={[
            { text: 'Yes, through Gusto', value: PayrollSetup.gusto },
            {
              text: 'Yes, through another provider',
              value: PayrollSetup.other,
            },
            {
              text: "No, I don't have payroll set up yet",
              value: PayrollSetup.none,
            },
          ]}
        />
        {/* <FormikRadioButton name={fieldNames.payrollSetup} label="Yes, through Gusto" value={PayrollSetup.gusto} schema={makeReqStringSchema()} />
        <FormikRadioButton name={fieldNames.payrollSetup} label="Yes, through another provider" value={PayrollSetup.other} schema={makeReqStringSchema()} />
        <FormikRadioButton name={fieldNames.payrollSetup} label="No, I don't have payroll set up yet" value={PayrollSetup.none} schema={makeReqStringSchema()} /> */}

        {values.payrollSetup === PayrollSetup.other && (
          <Form.Input
            name={fieldNames.otherProvider}
            label="Who is your payroll provider?"
            description="e.g. QuickBooks, OnPay, Square, etc."
            placeholder="Enter payroll provider name"
            shouldClearOnDismount
            schema={makeReqStringSchema()}
          />
        )}

        {values.payrollSetup && (
          <div>
            <FormikLabelError
              name={fieldNames.interestedInGep}
              label={interestedInGEPLabel}
              description={
                <>
                  <Text variant={TypographyStyles.BODY_MD} className="mt-2">
                    {interestedInGEPDescription}
                  </Text>
                  <Button
                    variant="link"
                    onClick={() => setInfoModalOpen(true)}
                    className="text-xs inline-block"
                    size="sm"
                  >
                    Learn more
                  </Button>
                </>
              }
              shouldClearOnDismount
              schema={makeReqStringSchema()}
            />
            <LearnMorePayrollModal
              open={infoModalOpen}
              onClose={() => setInfoModalOpen(false)}
            />
            <Form.BooleanRadioGroup
              className="mt-[-4px]"
              name={fieldNames.interestedInGep}
              schema={makeReqStringSchema()}
              shouldClearOnDismount
            />
          </div>
        )}

        {values.interestedInGep && (
          <div className="flex flex-col gap-1">
            {values.payrollSetup === PayrollSetup.gusto && (
              <Form.Select
                name={fieldNames.gustoPlan}
                options={[
                  { value: GustoPlan.simple, text: 'Simple' },
                  { value: GustoPlan.plus, text: 'Plus' },
                  { value: GustoPlan.premium, text: 'Premium' },
                  { value: GustoPlan.concierge, text: 'Concierge' },
                  { value: GustoPlan.contractor, text: 'Contractor Only' },
                  { value: GustoPlan.notSure, text: "I don't know" },
                ]}
                schema={makeReqStringSchema()}
                label="Which plan do you currently have with Gusto?"
                placeholder="Select Plan"
              />
            )}

            {isScorp && (
              <>
                <br />
                <FormikLabelError
                  name={fieldNames.needPayrollForOthers}
                  label="Do you need payroll for anyone else besides you?"
                  description="All S corporations are required to pay themselves through payroll to be compliant."
                  shouldClearOnDismount
                  schema={makeReqStringSchema()}
                />

                <div className="flex gap-4">
                  <Form.BooleanRadioGroup
                    name={fieldNames.needPayrollForOthers}
                    schema={makeReqStringSchema()}
                    shouldClearOnDismount
                  />
                </div>
              </>
            )}

            {(!isScorp || values.needPayrollForOthers) && (
              <>
                <LabelDescription
                  label={
                    isScorp
                      ? 'Who else do you need payroll for?'
                      : 'Who do you need payroll for?'
                  }
                  description={`This will add ${perPersonCost}. Select all that apply.`}
                />

                {!isScorp && (
                  <Form.Checkbox
                    name={fieldNames.needPayrollForSelf}
                    label="Me"
                    shouldClearOnDismount
                  />
                )}

                <Form.Checkbox
                  name={fieldNames.needPayrollForEmployees}
                  label="W-2 employees"
                  shouldClearOnDismount
                />
                <Form.Checkbox
                  name={fieldNames.needPayrollForContractors}
                  label="Contractors"
                  shouldClearOnDismount
                />
                <Form.Checkbox
                  name={fieldNames.needPayrollForOtherCheckbox}
                  label="Other"
                  shouldClearOnDismount
                />
                {values.needPayrollForOtherCheckbox && (
                  <Form.Input
                    name={fieldNames.needPayrollForOther}
                    placeholder="Enter who you need payroll for"
                    shouldClearOnDismount
                    schema={makeReqStringSchema()}
                  />
                )}
              </>
            )}

            {values.payrollSetup === PayrollSetup.other && (
              <>
                <FormikLabelError
                  name={fieldNames.usedGustoInPast}
                  label="Have you used Gusto for payroll in the past?"
                  shouldClearOnDismount
                  schema={makeReqStringSchema()}
                />
                <Form.Select
                  name={fieldNames.usedGustoInPast}
                  label="Have you used Gusto in the past?"
                  schema={makeReqStringSchema()}
                  options={[
                    {
                      text: 'Yes, as an employee of another business',
                      value: UsedPayrollInPast.yesAsEmployee,
                    },
                    {
                      text: 'Yes, for my current business',
                      value: UsedPayrollInPast.yesForCurrentBusiness,
                    },
                    {
                      text: 'Yes, for a previous business',
                      value: UsedPayrollInPast.yesForPreviousBusiness,
                    },
                    { text: 'No', value: UsedPayrollInPast.no },
                  ]}
                />
              </>
            )}

            {values.payrollSetup &&
              [PayrollSetup.gusto, PayrollSetup.other].includes(
                values.payrollSetup
              ) && (
                <>
                  <br />
                  <LabelDescription label="Select any features you are currently using." />
                  <div className="flex items-center">
                    <Form.Checkbox
                      name={fieldNames.multiplePayRates}
                      label="Multiple pay rates"
                      shouldClearOnDismount
                    />
                    <Popup content="You currently have an employee that you need to pay at different hourly rates." />
                  </div>

                  {values.payrollSetup === PayrollSetup.gusto && (
                    <div className="flex items-center">
                      <Form.Checkbox
                        name={fieldNames.gustoSponsoredBenefits}
                        label="Gusto-sponsored benefits"
                        shouldClearOnDismount
                      />
                      <Popup content="You currently have benefits administered through Gusto (e.g. health insurance administration, 401k contributions, HSA or FSA, etc.)." />
                    </div>
                  )}
                  <div className="flex items-center my-2">
                    <Form.Checkbox
                      name={fieldNames.timeTracking}
                      label="Time tracking"
                      shouldClearOnDismount
                    />
                  </div>
                  <div className="flex items-center my-2">
                    <Form.Checkbox
                      name={fieldNames.commissionOnlyEmployees}
                      label="100% commission employees"
                      shouldClearOnDismount
                    />
                  </div>
                </>
              )}
          </div>
        )}

        {values.interestedInGep === false && (
          <>
            <Text variant={TypographyStyles.H2}>Share your feedback</Text>

            <Form.Select
              name={fieldNames.notInterestedReason}
              options={[
                ...(hasFreePayrollWithSubscription
                  ? []
                  : [
                      {
                        value: PayrollNotInterestedReason.price,
                        text: 'Price',
                      },
                    ]),
                {
                  value: PayrollNotInterestedReason.missingFeatures,
                  text: 'Need more than streamlined basics',
                },
                {
                  value: PayrollNotInterestedReason.stayingWithCurrentProvider,
                  text: 'Staying with current provider',
                },
                { value: PayrollNotInterestedReason.other, text: 'Other' },
              ]}
              label="What would make Heard Payroll a better fit for you?"
              description="We appreciate your feedback. This will be used to improve our services."
              placeholder="Select an option"
              shouldClearOnDismount
            />

            {values.notInterestedReason ===
              PayrollNotInterestedReason.missingFeatures && (
              <FormikTextArea
                name={fieldNames.missingFeaturesNotInterestedReason}
                label="What features do you need?"
                shouldClearOnDismount
                schema={makeReqStringSchema()}
              />
            )}

            {values.notInterestedReason ===
              PayrollNotInterestedReason.other && (
              <FormikTextArea
                name={fieldNames.otherNotInterestedReason}
                label="Please share why"
                shouldClearOnDismount
                schema={makeReqStringSchema()}
              />
            )}
          </>
        )}

        <br />
        <FormFlowFooter
          onBack={() => setScreen(PayrollInterestScreenName.INTRO)}
          loading={isSubmitting}
          continueDisabled={isSubmitting}
          onForward={submitForm}
        />
      </div>
    </FormikProvider>
  )
}
